import React, {useEffect, useState} from 'react';
import {Route, Switch, useLocation} from "wouter";
import './App.css';
import Home from './components/Home';
import Form from './components/Form';
import {CaseInfo, FormItem, FormQuestion, NumberQuestion, Solutions} from './rest';
import {
  completeCase,
  currentForm,
  evaluation,
  getCurrentSolutions,
  homeForm,
  loadCase,
  nextForm,
  previousForm
} from './api';
import Solution from './components/Solution';
import Card, {CardDetails} from './components/Card';

const COMPLETE_CASE = "Maßnahme abschließen ✓";
export const NEW_CASE = 'Neue Maßnahme »';
export const NEXT_BUTTON_DEFAULT = 'Weiter »';
export const REPLAY_BUTTON_DEFAULT = 'Bearbeiten »';
export const PREVIOUS_BUTTON_DEFAULT = "« Zurück";
export const CONTINUE_LATER_BUTTON_DEFAULT = 'Später fortsetzen';
export const CONTINUE_CASE = "Fortsetzen »";

const App = () => {
  const [form, setForm] = useState<FormItem | undefined>(undefined);
  const [loadedCase, setLoadedCase] = useState<CaseInfo | undefined>(undefined);
  const [solution, setSolution] = useState<Solutions | undefined>(undefined);
  const [title, setTitle] = useState<string>("");
  const [location, setLocation] = useLocation();
  const [nextColorClass, setNextColorClass] = useState<string>("btn-light");
  const [activeDescriptions, setDescriptions] = useState<CardDetails | undefined>(undefined);
  const [canClickNext, setCanClickNext] = useState<boolean>(true);
  const [nextButtonTitle, setNextButtonTitle] = useState<string>(NEXT_BUTTON_DEFAULT);
  const [replayButtonEnabled, setReplayButtonEnabled] = useState<boolean>(false);
  const [background, setBackground] = useState<string>("");

  let basePath = window.location.pathname.substring(0, window.location.pathname.lastIndexOf("/"));
  let subLocation = location.substring(basePath.length);

  const navigateTo = React.useCallback((path: string) => {
    setLocation(basePath + path + window.location.search);
  }, [basePath, setLocation]);

  // runs once at startup...
  useEffect(() => {
    let doEffect = async () => {
      // if the case is not yet saved, e.g. because the session was reset or server restarted go back to home
      const form: FormItem = (await currentForm()).payload;
      if (!form.caseInfo.isSaved) {
        navigateTo("/")
      }
    };
    doEffect();
  }, [navigateTo])

  useEffect(() => {
    if (subLocation === "/solution" && solution && solution.suggested.filter(solutionItem => solutionItem.selected).length > 0) {
      setNextColorClass("list-group-item-warning");
    } else {
      setNextColorClass("btn-light");
    }
  }, [solution, subLocation]);

  useEffect(() => {
    // update background image, if available
    setBackground((form && form.image) ? form.image + window.location.search : "");

    const filterValue = (q: FormQuestion) => form!.answers[q.name] === undefined || form!.answers[q.name] === null

    // update next button
    if (subLocation === "/form" || subLocation === "/evaluation") {
      if (form && form.questions.filter(q => !q.optional).filter(filterValue).length !== 0) {
        setCanClickNext(false);
      } else if (form &&
        form.questions
          .filter(q => q.kind === "NUMBER")
          .filter(q => !filterValue(q))
          .filter(q => form!.answers[q.name] < (q as NumberQuestion).min || form!.answers[q.name] > (q as NumberQuestion).max)
          .length > 0) {
        setCanClickNext(false);
      } else {
        setCanClickNext(true);
      }
    } else if (subLocation === "/solution" && solution) {
      if (solution.selected.length > 0 || solution.established.filter(si => si.selected).length > 0 || solution.suggested.filter(si => si.selected).length > 0) {
        setCanClickNext(true);
      } else {
        setCanClickNext(false);
      }
    } else {
      setCanClickNext(true);
    }

  }, [subLocation, form, solution]);

  useEffect(() => {
    if (subLocation === "/evaluation") {
      setNextButtonTitle(COMPLETE_CASE);
    } else {
      setNextButtonTitle(NEXT_BUTTON_DEFAULT);
    }
  }, [subLocation]);

  useEffect(() => {
    const handleReturnedForm = (returnedForm: FormItem) => {
      setLoadedCase(returnedForm.caseInfo);
      setForm(returnedForm);
      setTitle(returnedForm.title);
    };

    const doEffect = async () => {
      if (subLocation === "/") {
        handleReturnedForm((await homeForm()).payload);
      } else if (subLocation === "/form") {
        handleReturnedForm((await currentForm()).payload);
      } else if (subLocation === "/solution") {
        const returnedSolution = (await getCurrentSolutions()).payload;
        setLoadedCase(returnedSolution.caseInfo);
        setSolution(returnedSolution)
        setTitle(returnedSolution.title);
      } else if (subLocation === "/evaluation") {
        handleReturnedForm((await evaluation()).payload);
      }
    };
    doEffect();
  }, [subLocation]);

  const previous = async () => {
    if (subLocation === "/form") {
      let goToHome = false;
      if (form && form.nr === 1) {
        goToHome = true;
      }
      const returnedForm: FormItem = (await previousForm()).payload;
      setForm(returnedForm);
      setTitle(returnedForm.title);
      if (goToHome) {
        navigateTo("/");
      }
    } else if (subLocation === "/solution") {
      const returnedForm: FormItem = (await previousForm()).payload;
      setForm(returnedForm);
      setTitle(returnedForm.title);
      navigateTo("/form")
    } else if (subLocation === "/evaluation") {
      if (form && form.title !== "EMPTY") {
        const returnedForm: FormItem = (await previousForm()).payload;
        setForm(returnedForm);
      }
      setSolution(await (await getCurrentSolutions()).payload);
      setTitle("Empfehlungen");
      navigateTo("/solution");
    }
    setDescriptions(undefined);
  }

  const next = async () => {
    if (form) {
      if (subLocation === "/") {
        if (!form.answers[form.caseInfo.caseIdName] || !form.answers[form.caseInfo.caseDateName]) {
          return alert(`Bitte vergeben Sie ein ${form.caseInfo.caseIdName} und ein ${form.caseInfo.caseDateName}.`);
        }
        navigateTo("/form");
      }

      if (subLocation === "/form" || (subLocation === "/")) {
        const returnedForm: FormItem = (await nextForm()).payload;
        if (returnedForm.title === "EMPTY") {
          setSolution(await (await getCurrentSolutions()).payload)
          setTitle("Empfehlungen");
          navigateTo("/solution");
        } else {
          setForm(returnedForm);
          setTitle(returnedForm.title);
          navigateTo("/form");
        }
      } else if (subLocation === "/solution") {
        const returnedForm: FormItem = (await nextForm()).payload;
        setForm(returnedForm);
        setTitle(returnedForm.title);
        navigateTo("/evaluation");
      } else if (subLocation === "/evaluation") {
        if ((await completeCase()).payload) {
          navigateTo("/");
        }
      }
    }
    setDescriptions(undefined);
  }

  const replay = async () => {
    if (loadedCase) {
      loadedCase.isReplay = true;
      let form = (await loadCase(loadedCase)).payload;
      if (form?.caseInfo) setLoadedCase(form.caseInfo);
      navigateTo("/form");
      setDescriptions(undefined);
    }
  }

  const continueLater = async () => {
    navigateTo("/");
    setDescriptions(undefined);
  }

  let isReplay = loadedCase?.isReplay || false;
  let homeFormElement = form ? <Home form={form} isReplay={isReplay} setForm={setForm}
                                     cardDetails={activeDescriptions}
                                     setNextButtonTitle={setNextButtonTitle}
                                     setReplayButtonEnabled={setReplayButtonEnabled}
                                     setLoadedCase={setLoadedCase}
                                     setCardDetails={setDescriptions}/> : <div/>;
  return (

    <div className="content">
      <div className="background" style={{backgroundImage: `url(${background})`}}/>
      <div className="navigation-pane">
        <ul>
          <li>
            {(subLocation === "/")
              ? <div/>
              : <button className="btn btn-light" onClick={previous}>{PREVIOUS_BUTTON_DEFAULT}</button>}
            {subLocation !== "/"
              ? <button className={`btn continue-later-btn ${nextColorClass}`}
                        onClick={continueLater}>{CONTINUE_LATER_BUTTON_DEFAULT}</button>
              : null
            }
          </li>
          <li>
            <h3 className="text-white navigation-title">{title}</h3>
          </li>
          <li>
            {subLocation === "/" && replayButtonEnabled
              ? <button className={`btn replay-btn ${nextColorClass}`} onClick={replay}>{REPLAY_BUTTON_DEFAULT}</button>
              : null
            }
            <button disabled={!canClickNext} className={`btn ${nextColorClass}`}
                    onClick={next}>{nextButtonTitle}</button>
          </li>
        </ul>
      </div>
      <div className="content-pane">
        <Switch>
          <Route path={`${basePath}/`}>
            {() => homeFormElement}
          </Route>
          <Route path={`${basePath}/form`}>
            {() => form ? <div className="form-container"><Form form={form} isReplay={isReplay} setForm={setForm}
                                                                setCardDetails={setDescriptions}/><Card
              details={activeDescriptions} clear={setDescriptions}/></div> : <div/>}
          </Route>
          <Route path={`${basePath}/solution`}>
            {() => solution ?
              <div className="solution-container"><Solution form={form} isReplay={isReplay} setForm={setForm}
                                                            solutions={solution}
                                                            setSolution={setSolution}
                                                            setCardDetails={setDescriptions}/> <Card
                details={activeDescriptions} clear={setDescriptions}/></div> : <div/>}
          </Route>
          <Route path={`${basePath}/evaluation`}>
            {() => form ? <div className="form-container"><Form form={form} isReplay={isReplay} setForm={setForm}
                                                                setCardDetails={setDescriptions}/><Card
              details={activeDescriptions} clear={setDescriptions}/></div> : <div/>}
          </Route>
        </Switch>
      </div>
    </div>
  )

}

export async function confirmIfReplay(isReplay: boolean, setFact: () => Promise<void>) {
  if (isReplay) {
    if (window.confirm("Dies verändert einen bereits abgeschlossenen Fall. Möchten Sie fortfahren?")) {
      await setFact();
    }
  } else {
    await setFact();
  }
}


export default App;
